import { Component } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';

import { DocumentService } from '../../../core/api/document.service';
import { IneligibleAreaUserService } from '../../../core/api/ineligible-area-user.service';
import { PremiumAcreageUserService } from '../../../core/api/premium-acreage-user.service';
import { ProspectiveUserService } from '../../../core/api/prospective-user.service';
import { SiteService } from '../../../core/api/site.service';
import { CsvService } from '../../../core/helper/csv.service';
import { DialogYesNoComponent } from '../../../shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { SpinnerService } from '../../../shared/spinner/spinner.service';

@Component({
  selector: 'fc-admin-utilities',
  templateUrl: './admin-utilities.component.html',
  styleUrls: ['./admin-utilities.component.scss'],
})
export class AdminUtilitiesComponent {
  adminForm: UntypedFormGroup;

  constructor(
    readonly documentService: DocumentService,
    readonly formBuilder: UntypedFormBuilder,
    readonly spinnerService: SpinnerService,
    readonly prospectiveUserService: ProspectiveUserService,
    readonly siteService: SiteService,
    readonly ineligibleUserService: IneligibleAreaUserService,
    readonly premiumAcreageUserService: PremiumAcreageUserService,
    readonly csvService: CsvService,
    public dialog: MatDialog
  ) {
    this.adminForm = this.formBuilder.group({
      threshold: ['.03'],
    });
  }

  deleteOrphans() {
    this.documentService.getOrphans().subscribe((orphanCount: number) => {
      if (orphanCount > 0) {
        this.dialog
          .open(DialogYesNoComponent, {
            panelClass: 'fc-yes-no-dialog',
            data: {
              title: 'Orphaned Documents Found',
              line1: `A total of ${orphanCount} orphaned documents were found. Would you like to permanently delete these?`,
              titleAlign: 'left',
              buttonTextNo: 'No, Cancel',
              buttonTextYes: 'Yes, Delete',
            },
          })
          .afterClosed()
          .subscribe((doDelete: boolean) => {
            if (doDelete) {
              this.documentService.deleteOrphans().subscribe((deleteCount: number) => {
                const data = {
                  title: 'Finished Deleting Documents',
                  line1: `${deleteCount} documents out of ${orphanCount} were deleted`,
                  buttonTextYes: 'OK',
                  line2:
                    deleteCount < orphanCount
                      ? 'There are still orphaned documents to delete. Please repeat this process.'
                      : null,
                };
                this.dialog.open(DialogYesNoComponent, {
                  panelClass: 'fc-yes-no-dialog',
                  data,
                });
              });
            }
          });
      } else {
        this.dialog.open(DialogYesNoComponent, {
          panelClass: 'fc-yes-no-dialog',
          data: {
            title: 'No Orphaned Documents Found',
            line1: 'The document store has no orphaned documents',
            buttonTextYes: 'OK',
          },
        });
      }
    });
  }

  exportProspectiveUsersCSV(): void {
    this.prospectiveUserService.getProspectiveUsersCSV().subscribe(csvString => {
      this.csvService.exportCSV(csvString, 'Prospective_Users');
    });
  }

  exportPremiumAcreageUsersCSV(): void {
    this.premiumAcreageUserService.getPremiumAcreageUsersCSV().subscribe(csvString => {
      this.csvService.exportCSV(csvString, 'Premium_Acreage_Users');
    });
  }

  exportIneligibleUsersCSV(): void {
    this.ineligibleUserService.getIneligibleUserCSV().subscribe(csvString => {
      this.csvService.exportCSV(csvString, 'Ineligible_Users');
    });
  }

  checkOwnership() {
    this.spinnerService.show('app-spinner', true, 'Detecting Owner Changes');
    this.siteService.getOwnershipChangeCSV().subscribe(csvString => {
      this.spinnerService.hide('app-spinner');
      this.csvService.exportCSV(csvString, 'Ownership_change');
    });
  }

  checkChangeDetection() {
    this.spinnerService.show('app-spinner', true, 'Finding Area Changes');
    this.siteService.getChangeDetectionCSV(this.adminForm.get('threshold').value).subscribe(csvString => {
      this.spinnerService.hide('app-spinner');
      this.csvService.exportCSV(csvString, 'Change_Detection');
    });
  }
}
